import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, noop, uniq } from 'lodash';
import { dollarAmount } from 'src/common/display/Money/DollarAmount';
import getRemainingAuthorizedAmountPerInvoice from 'actions/Contact/Group/getRemainingAuthorizedAmountPerInvoice';
import { connect } from 'react-redux';
import WorkqueueBulkAction from 'common/Workqueue/WorkqueueBulkAction';
import { useBulkUpdateInvoiceStatus } from '../../hooks/useUpdateInvoice';
import { useFind } from '../../../../api/APIHooks';
import { InvoiceBulkActionsModalRows } from './InvoiceBulkActionsModalRows';
import {
  getHasInvoicesAboveRemainingAuth,
  getCanBulkActionAllInvoices,
  getErroredInvoiceIds,
} from '../../utils/bulkActionAuthUtils';

const BulkAcceptInvoices = ({
  deselectAll,
  deselectRows,
  selectedRows,
  invoiceIds,
  getRemainingAuthorizedAmountPerInvoice: getAvailableFundsPerInvoice,
  loadingInvoicedSpends,
  invoicedSpends,
}) => {
  const [bulkActionsFormOpen, setBulkActionsFormOpen] = useState(false);

  const iwqBulkAcceptFormRef = useRef();
  const disableBulkAcceptButton = !(selectedRows.length > 0 &&
    selectedRows.every((i) => i.invoice_status === 'transmitted_to_payer'));

  const closeBulkAcceptForm = () => {
    setBulkActionsFormOpen(false);
    iwqBulkAcceptFormRef.current.closeDialog();
  };

  const openBulkAcceptForm = () => {
    setBulkActionsFormOpen(true);
    iwqBulkAcceptFormRef.current.openDialog();
  };

  const bulkAcceptInvoices = useBulkUpdateInvoiceStatus(
    invoiceIds,
    'accepted_by_payer',
  );

  const bulkStatus = !isEmpty(selectedRows) ?
    selectedRows[0].invoice_status :
    'No Invoices Selected';

  const totalBulkInvoicesAmount = !isEmpty(selectedRows) ?
    dollarAmount(selectedRows.reduce((acc, invoice) => acc + invoice?.total_amount_invoiced, 0), true) : 0;

   const submitBulkAccceptedInvoices = async () => {
    await bulkAcceptInvoices();
    closeBulkAcceptForm();
    deselectAll();
  };

  useEffect(() => {
    if (bulkActionsFormOpen && bulkStatus === 'transmitted_to_payer') {
      const invoicesWithAuthorization = selectedRows.filter((invoice) => !!invoice.service_authorization);
      getAvailableFundsPerInvoice(invoicesWithAuthorization);
    }
  }, [selectedRows, bulkActionsFormOpen, bulkStatus]);

  const fspIds = uniq(selectedRows.map((invoice) => invoice.fee_schedule_program_id));
  const { isSuccess: isFSPsLoaded, data } = useFind(
    'fee_schedule_programs',
    { id: fspIds.join() },
    { queryConfig: { placeholderData: undefined } },
  );
  const fsps = get(data, 'data.data', []);
  const invoiceFsps = Object.fromEntries(selectedRows.map((invoice) => [
    invoice.id, fsps.find((fsp) => invoice.fee_schedule_program_id === fsp.id),
  ]));

  const hasInvoicesAboveRemainingAuthorizedAmount =
    getHasInvoicesAboveRemainingAuth(invoicedSpends);
  const canAcceptAllInvoices =
    getCanBulkActionAllInvoices(selectedRows, invoicedSpends, invoiceFsps);
  const erroredInvoiceIds =
    getErroredInvoiceIds(selectedRows, invoicedSpends, invoiceFsps);

  const invoicesNotAuthorizedToSubmit = selectedRows.length === 0 ||
    (hasInvoicesAboveRemainingAuthorizedAmount && !canAcceptAllInvoices) ||
    loadingInvoicedSpends || !isFSPsLoaded;

  return (
    <WorkqueueBulkAction
      action={'Accept'}
      open={openBulkAcceptForm}
      close={closeBulkAcceptForm}
      onSubmit={submitBulkAccceptedInvoices}
      bulkActionIcon="CheckCircle"
      dialogTitle="Accept Selected Invoices"
      confirmLabel="Accept"
      dialogRef={iwqBulkAcceptFormRef}
      disableOpenModal={disableBulkAcceptButton}
      selectedRows={selectedRows}
      deselectAll={deselectAll}
      disableSubmitRowsButton={invoicesNotAuthorizedToSubmit}
    >
      <>
        <p className="mx-3 my-4">
          You are about to mark the following invoices as
          <strong> accepted </strong>for a total of{' '}
          <strong>{totalBulkInvoicesAmount}</strong>. Would you like to continue?
        </p>
        <div className="my-6">
          <InvoiceBulkActionsModalRows
            invoices={selectedRows}
            deselectRows={deselectRows}
            erroredInvoiceIds={erroredInvoiceIds}
          />
          {hasInvoicesAboveRemainingAuthorizedAmount && (
          <p className="text-red text-13px pl-5 mt-5">
            {canAcceptAllInvoices ?
              'The remaining authorized amount is less than the invoice amount for one or more invoices.' :
              'One or more of the invoices cannot be accepted because the remaining authorized amount is ' +
              'less than the invoice amount.'}
          </p>
        )}
        </div>
      </>
    </WorkqueueBulkAction>
  );
};

BulkAcceptInvoices.propTypes = {
  deselectAll: PropTypes.func.isRequired,
  deselectRows: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired,
  invoiceIds: PropTypes.array.isRequired,
  invoicedSpends: PropTypes.object.isRequired,
  getRemainingAuthorizedAmountPerInvoice: PropTypes.func,
  loadingInvoicedSpends: PropTypes.bool.isRequired,
};

BulkAcceptInvoices.defaultProps = {
  getRemainingAuthorizedAmountPerInvoice: noop,
};

function mapStateToProps(state) {
  const networkId = state.networks.networkId;
  return {
    networkId,
    invoicedSpends: get(state, 'invoicedSpend.invoicedSpends'),
    loadingInvoicedSpends: get(state, 'invoicedSpend.isFetching'),
  };
}

export { BulkAcceptInvoices };
export default connect(
mapStateToProps,
  { getRemainingAuthorizedAmountPerInvoice },
)(
  BulkAcceptInvoices,
);
